import type {
	CollectionSortFilterOptions,
	MediaFilterOptions,
	RatingFilterOptions,
	StatusFilterOptions,
} from './collectionFilters.js'

export type FilterValue<T> = T | T[]

export type CollectionFilterQuery = {
	sort?: CollectionSortFilterOptions
	search?: string
	favoritesOnly?: boolean
	media?: FilterValue<MediaFilterOptions>
	rating?: FilterValue<RatingFilterOptions>
	folderId?: FilterValue<number> | null
	status?: FilterValue<StatusFilterOptions>
	genre?: FilterValue<string>
	style?: FilterValue<string>
	yearFrom?: number
	yearTo?: number
	includeHidden?: boolean
}

type CollectionMatchFilterQuery = Pick<
	CollectionFilterQuery,
	| 'favoritesOnly'
	| 'media'
	| 'rating'
	| 'status'
	| 'genre'
	| 'style'
	| 'yearFrom'
	| 'yearTo'
>

/** Appends canonical collection filter values to an existing URLSearchParams object. */
export function appendCollectionFilterParams(
	params: URLSearchParams,
	query: CollectionFilterQuery,
): URLSearchParams {
	if (query.sort) params.set('sort', query.sort)
	const search = query.search?.trim()
	if (search) params.set('search', search)
	if (query.favoritesOnly) params.set('favoritesOnly', 'true')
	appendValues(params, 'media', query.media, 'all')
	appendValues(params, 'rating', query.rating)
	appendValues(params, 'folderId', query.folderId)
	appendValues(params, 'status', query.status, 'all')
	appendValues(params, 'genre', query.genre)
	appendValues(params, 'style', query.style)
	if (typeof query.yearFrom === 'number')
		params.set('yearFrom', String(query.yearFrom))
	if (typeof query.yearTo === 'number')
		params.set('yearTo', String(query.yearTo))
	if (query.includeHidden) params.set('includeHidden', 'true')
	return params
}

/** Builds the Mongo match stages shared by collection browsing and shuffle. */
export function collectionFilterMatchStages(
	filters: CollectionMatchFilterQuery,
): Record<string, unknown>[] {
	const stages: Record<string, unknown>[] = []
	const mediaStage = mediaMatchStage(filters.media)
	const statusStage = statusMatchStage(filters.status)
	if (mediaStage) stages.push(mediaStage)
	const ratings = activeValues(filters.rating)
	if (ratings.length) stages.push({ $match: { rating: { $in: ratings } } })
	if (statusStage) stages.push(statusStage)
	if (filters.favoritesOnly) {
		stages.push({ $match: { 'favorite.0': { $exists: true } } })
	}

	if (
		typeof filters.yearFrom === 'number' ||
		typeof filters.yearTo === 'number'
	) {
		const yearMatch: Record<string, number> = {}
		if (typeof filters.yearFrom === 'number')
			yearMatch.$gte = filters.yearFrom
		if (typeof filters.yearTo === 'number') yearMatch.$lte = filters.yearTo
		stages.push({ $match: { 'masterDoc.year': yearMatch } })
	}
	const genres = activeValues(filters.genre)
	if (genres.length)
		stages.push({ $match: { 'basicDoc.genres': { $in: genres } } })
	const styles = activeValues(filters.style)
	if (styles.length)
		stages.push({ $match: { 'basicDoc.styles': { $in: styles } } })

	return stages
}

/** Builds the Mongo match stage for the selected physical media format. */
export function mediaMatchStage(
	media?: FilterValue<MediaFilterOptions>,
): Record<string, unknown> | null {
	const mediaValues = activeValues(media, 'all')
	if (!mediaValues.length) return null

	const predicates = mediaValues.map(mediaMatchPredicate)
	if (predicates.length === 1) return { $match: predicates[0] }
	return { $match: { $or: predicates } }
}

function mediaMatchPredicate(
	media: MediaFilterOptions,
): Record<string, unknown> {
	if (media === 'Box Set') {
		return {
			'basicDoc.formats': {
				$elemMatch: {
					$or: [
						{
							name: {
								$regex: 'box[-\\s]*set',
								$options: 'i',
							},
						},
						{
							descriptions: {
								$regex: 'box[-\\s]*set',
								$options: 'i',
							},
						},
						{
							text: {
								$regex: 'box[-\\s]*set',
								$options: 'i',
							},
						},
					],
				},
			},
		}
	}

	if (media === 'Single') {
		return {
			'basicDoc.formats': {
				$elemMatch: {
					$or: [
						{ name: { $regex: '^single$', $options: 'i' } },
						{
							descriptions: {
								$regex: '^single$',
								$options: 'i',
							},
						},
						{
							descriptions: {
								$regex: '^7\\s*\"$',
								$options: 'i',
							},
						},
						{
							descriptions: {
								$regex: '^7\\s*inch$',
								$options: 'i',
							},
						},
						{
							text: {
								$regex: '7\\s*(\"|inch)',
								$options: 'i',
							},
						},
					],
				},
			},
		}
	}

	return { 'basicDoc.formats.name': media.toLowerCase() }
}

/** Builds the Mongo match stage for listened, not-listened, or hidden status filters. */
export function statusMatchStage(
	status?: FilterValue<StatusFilterOptions>,
): Record<string, unknown> | null {
	const statuses = activeValues(status, 'all')
	if (!statuses.length) return null
	const predicates = statuses.map(statusMatchPredicate)
	if (predicates.length === 1) return { $match: predicates[0] }
	return { $match: { $or: predicates } }
}

/** Builds the default Mongo match stage that removes hidden releases from non-hidden views. */
export function excludeHiddenStage(
	status?: FilterValue<StatusFilterOptions>,
): Record<string, unknown> | null {
	if (activeValues(status, 'all').includes('hidden')) return null
	return {
		$match: {
			$or: [{ hist: { $size: 0 } }, { 'hist.isHidden': { $ne: true } }],
		},
	}
}

function statusMatchPredicate(
	status: StatusFilterOptions,
): Record<string, unknown> {
	if (status === 'listened') return { historyCount: { $gt: 0 } }
	if (status === 'notListened') return { historyCount: 0 }
	if (status === 'hidden') return { 'hist.isHidden': true }
	return {}
}

function appendValues<T extends string | number>(
	params: URLSearchParams,
	key: string,
	value: FilterValue<T> | null | undefined,
	emptyValue?: T,
): void {
	if (value == null) return
	const values = (Array.isArray(value) ? value : [value]).filter(
		(item) => item !== emptyValue,
	)
	for (const item of values) {
		params.append(key, String(item))
	}
}

function activeValues<T extends string | number>(
	value: FilterValue<T> | null | undefined,
	emptyValue?: T,
): T[] {
	if (value == null) return []
	return (Array.isArray(value) ? value : [value]).filter(
		(item) => item !== emptyValue,
	)
}
